import { useProfile } from 'hooks'
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react'
import { useClinicServices } from 'services'
import { socketIoService } from 'socket'
import styled from 'styled-components'

const OnlineStatusContext = createContext<boolean | undefined>(undefined)

export const useOnlineStatus = () => {
  const context = useContext(OnlineStatusContext)
  if (context === undefined) {
    throw new Error(
      'useOnlineStatus must be used within an OnlineStatusProvider'
    )
  }
  return context
}

const StyledCenteredNotification = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #ff0042;
  color: #ffffff;
  text-align: center;
  padding: 10px 0;
  z-index: 9999;
`

interface OnlineStatusProviderProps {
  children: ReactNode
}

export const OnlineStatusProvider: React.FC<OnlineStatusProviderProps> = ({
  children
}) => {
  const [isOnline, setOnline] = useState<boolean>(navigator.onLine)
  const { getClinicInfo } = useClinicServices()
  const { isAuth } = useProfile()

  const checkCallApi = async () => {
    try {
      const res = await getClinicInfo()
      return res._statusRes >= 200 && res._statusRes < 300
    } catch (error) {
      return false
    }
  }

  const updateNetworkStatus = async () => {
    const online = await checkCallApi()
    if (online) {
      if (!isOnline) {
        setOnline(true)
      }
    } else {
      if (isOnline) {
        setOnline(false)
      }
    }
  }

  const getInstanceSocket = () => {
    const socket = socketIoService.getSocketConnection()
    if (!socket) {
      socketIoService.initSocket()
      return socketIoService.getSocketConnection()
    }
    return socket
  }

  useEffect(() => {
    const socket = getInstanceSocket()

    if (!socket) {
      console.error('Socket connection is not established')
      return
    }

    const handleOnline = () => updateNetworkStatus()
    const handleOffline = () => updateNetworkStatus()
    const handleSocketConnect = () => updateNetworkStatus()
    const handleSocketDisconnect = () => updateNetworkStatus()

    window.addEventListener('online', handleOnline)
    socket.on('connect', handleSocketConnect)
    window.addEventListener('offline', handleOffline)
    socket.on('disconnect', handleSocketDisconnect)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      socket.off('connect', handleSocketConnect)
      socket.off('disconnect', handleSocketDisconnect)
    }
  }, [isOnline])

  const isLoginOrRegisterPage =
    window.location.pathname === '/login' ||
    window.location.pathname === '/register'

  return (
    <OnlineStatusContext.Provider value={isOnline}>
      {children}
      {!isOnline && !isLoginOrRegisterPage && isAuth && (
        <StyledCenteredNotification>
          <p>
            Không kết nối được đến máy chủ, có thể lỗi máy chủ hoặc kết nối
            mạng. Vui lòng kiểm tra kết nối.
          </p>
        </StyledCenteredNotification>
      )}
    </OnlineStatusContext.Provider>
  )
}
